interface SessionConfig {
  timeoutMinutes: number;
  warningMinutes: number;
  enableAuditLogging: boolean;
}

interface AuditLog {
  timestamp: string;
  event: string;
  userId: string;
  ipAddress: string;
  userAgent: string;
  details: any;
}

class SessionService {
  private static instance: SessionService;
  private config: SessionConfig;
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private warningId: ReturnType<typeof setTimeout> | null = null;
  private idleCheckIntervalId: ReturnType<typeof setInterval> | null = null;
  private lastActivity: number = Date.now();
  private auditLogs: AuditLog[] = [];
  private onTimeoutCallback: (() => void) | null = null;
  private currentTimeoutMs: number;
  private currentWarningMs: number;
  private readonly defaultWarningMs: number;
  private readonly fallbackWarningMs = 5000; // 5 seconds lead by default
  private isTimingOut = false;
  private warningActive = false;
  private lastDispatchedWarningSeconds: number | null = null;

  private constructor(config: SessionConfig) {
    this.config = config;
    this.currentTimeoutMs = config.timeoutMinutes * 60 * 1000;
    this.defaultWarningMs = config.warningMinutes * 60 * 1000;
    this.currentWarningMs = this.calculateWarningMs(
      this.currentTimeoutMs,
      Math.min(this.defaultWarningMs, this.fallbackWarningMs)
    );
    this.setupActivityListeners();
    this.startSessionTimer();
  }

  public static getInstance(config?: SessionConfig): SessionService {
    if (!SessionService.instance && config) {
      SessionService.instance = new SessionService(config);
    }
    return SessionService.instance;
  }

  public setOnTimeoutCallback(callback: () => void): void {
    this.onTimeoutCallback = callback;
  }

  private setupActivityListeners(): void {
    // Intentionally omit mousemove — it fires continuously in the viewer and never allows idle timeout.
    const activityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];

    activityEvents.forEach(event => {
      document.addEventListener(event, () => {
        this.updateLastActivity();
      }, true);
    });

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.updateLastActivity();
      }
    });
  }

  public updateLastActivity(force: boolean = false): void {
    if (this.warningActive && !force) {
      return;
    }

    this.lastActivity = Date.now();
    this.clearWarningState();
    this.resetSessionTimer();
  }

  public startSessionTimer(): void {
    this.lastActivity = Date.now();
    this.clearWarningState();
    this.startIdleCheck();
    this.resetSessionTimer();
  }

  public startSessionTimerWithDuration(durationMs: number): void {
    if (!durationMs || durationMs <= 0) {
      this.handleSessionTimeout();
      return;
    }

    this.currentTimeoutMs = durationMs;
    this.currentWarningMs = this.calculateWarningMs(
      this.currentTimeoutMs,
      Math.min(this.defaultWarningMs, this.fallbackWarningMs)
    );
    this.lastActivity = Date.now();
    this.clearWarningState();
    this.startIdleCheck();
    this.resetSessionTimer();
  }

  public updateTimeoutSeconds(seconds: number, warningLeadSeconds?: number): void {
    if (!seconds || seconds <= 0) {
      console.warn('SessionService: Invalid timeout seconds provided. Ignoring update.');
      return;
    }

    this.currentTimeoutMs = seconds * 1000;
    const desiredWarningMs = warningLeadSeconds !== undefined
      ? Math.max(0, warningLeadSeconds * 1000)
      : Math.min(this.defaultWarningMs, this.fallbackWarningMs);
    this.currentWarningMs = this.calculateWarningMs(this.currentTimeoutMs, desiredWarningMs);
    this.config.timeoutMinutes = seconds / 60;
    this.clearWarningState();
    this.startIdleCheck();
    this.resetSessionTimer();
  }

  public stopSessionTimer(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.warningId) {
      clearTimeout(this.warningId);
      this.warningId = null;
    }
    if (this.idleCheckIntervalId) {
      clearInterval(this.idleCheckIntervalId);
      this.idleCheckIntervalId = null;
    }
    this.clearWarningState();
  }

  private resetSessionTimer(): void {
    if (!this.currentTimeoutMs || this.currentTimeoutMs <= 0) {
      this.stopSessionTimer();
      return;
    }

    this.isTimingOut = false;
    this.warningActive = false;

    // Clear existing timers
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    if (this.warningId) {
      clearTimeout(this.warningId);
      this.warningId = null;
    }

    const warningDelay = this.getWarningDelay();
    if (warningDelay !== null) {
      this.warningId = setTimeout(() => {
        this.showSessionWarning();
      }, warningDelay);
    }

    this.timeoutId = setTimeout(() => {
      this.handleSessionTimeout();
    }, this.currentTimeoutMs);
  }

  private showSessionWarning(): void {
    if (this.warningActive) {
      return;
    }

    this.warningActive = true;
    this.logAuditEvent('SESSION_WARNING', 'Session timeout warning shown');
    this.dispatchWarningCountdown();
  }

  private handleSessionTimeout(): void {
    if (this.isTimingOut) {
      return;
    }

    this.isTimingOut = true;
    this.logAuditEvent('SESSION_TIMEOUT', 'Session timed out due to inactivity');

    // Call the timeout callback if set
    if (this.onTimeoutCallback) {
      this.onTimeoutCallback();
    } else {
      // Default behavior: redirect to login
      window.location.href = '/login?timeout=true';
    }

    this.clearWarningState();
  }

  public isSessionExpired(): boolean {
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivity;

    return timeSinceLastActivity > this.currentTimeoutMs;
  }

  public getSessionInfo() {
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivity;
    const timeRemaining = this.currentTimeoutMs - timeSinceLastActivity;

    return {
      lastActivity: this.lastActivity,
      timeSinceLastActivity,
      timeRemaining,
      isExpired: this.isSessionExpired(),
      timeoutMinutes: this.currentTimeoutMs / (60 * 1000),
      warningMinutes: this.currentWarningMs / (60 * 1000),
    };
  }

  private logAuditEvent(event: string, details: string): void {
    const auditLog: AuditLog = {
      timestamp: new Date().toISOString(),
      event,
      userId: 'unknown',
      ipAddress: '127.0.0.1', // In production, get from request
      userAgent: navigator.userAgent,
      details,
    };

    this.auditLogs.push(auditLog);

    // In production, send to secure audit log service
    console.log('AUDIT LOG:', auditLog);

    // Keep only last 1000 logs in memory
    if (this.auditLogs.length > 1000) {
      this.auditLogs = this.auditLogs.slice(-1000);
    }
  }

  public getAuditLogs(): AuditLog[] {
    return [...this.auditLogs];
  }

  private dispatchWarningCountdown(): void {
    const now = Date.now();
    const inactivityDuration = now - this.lastActivity;
    const remainingMs = this.currentTimeoutMs - inactivityDuration;
    const secondsRemaining = Math.max(0, Math.ceil(remainingMs / 1000));

    if (this.lastDispatchedWarningSeconds !== secondsRemaining) {
      this.lastDispatchedWarningSeconds = secondsRemaining;
      window.dispatchEvent(new CustomEvent('session-warning', {
        detail: { secondsRemaining },
      }));
    }

    if (secondsRemaining <= 0) {
      this.warningActive = false;
    }
  }

  private clearWarningState(): void {
    if (this.warningActive || this.lastDispatchedWarningSeconds !== null) {
      this.warningActive = false;
      this.lastDispatchedWarningSeconds = null;
      window.dispatchEvent(new CustomEvent('session-warning-clear'));
    }
  }

  private calculateWarningMs(timeoutMs: number, desiredWarningMs: number): number {
    if (!timeoutMs || timeoutMs <= 0) {
      return 0;
    }

    const maxAllowableWarning = Math.max(0, timeoutMs);
    if (maxAllowableWarning === 0) {
      return 0;
    }

    if (desiredWarningMs <= 0) {
      return 0;
    }

    return Math.min(desiredWarningMs, maxAllowableWarning);
  }

  private getWarningDelay(): number | null {
    if (!this.currentWarningMs || this.currentWarningMs <= 0 || this.currentWarningMs >= this.currentTimeoutMs) {
      return null;
    }

    return this.currentTimeoutMs - this.currentWarningMs;
  }

  private startIdleCheck(): void {
    if (this.idleCheckIntervalId) {
      clearInterval(this.idleCheckIntervalId);
    }

    if (!this.currentTimeoutMs || this.currentTimeoutMs <= 0) {
      return;
    }

    this.idleCheckIntervalId = setInterval(() => {
      const now = Date.now();
      const inactivityDuration = now - this.lastActivity;

      if (!this.warningActive && this.currentWarningMs > 0) {
        const warningThreshold = this.currentTimeoutMs - this.currentWarningMs;
        if (warningThreshold <= 0 || inactivityDuration >= warningThreshold) {
          this.showSessionWarning();
        }
      }

      if (this.warningActive) {
        this.dispatchWarningCountdown();
      }

      if (inactivityDuration >= this.currentTimeoutMs) {
        this.handleSessionTimeout();
      }
    }, 1000);
  }
}

export { SessionService };
